import { useDeferredValue, useState } from "react";
import { useConvex, useMutation, usePaginatedQuery, useQuery } from "convex/react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Copy, Download, Search, ShieldAlert, Users } from "lucide-react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { Badge, Button, Card, EmptyState, Input, Notice, PageHeader, ProgressBar, TableSkeleton } from "../components/ui";
import { CATEGORY_LABELS, STATUS_LABELS, errorMessage, formatDate, formatNumber, signalLabel } from "../lib/format";
import { banReason, copyText, downloadDiscordIds } from "../lib/banTools";
import { BanReasonPicker } from "../components/BanReasonPicker";
import { describeEvasionProof } from "../lib/evasionProof";

const RUNNING = ["queued", "ingesting", "running"];
/** Les identifiants Convex sont des chaînes courtes en base 32 : on écarte les URL manifestement fausses. */
const ID_PATTERN = /^[a-z0-9]{20,40}$/;

export function CheckDetailPage() {
  const convex = useConvex();
  const { checkId } = useParams();
  const valid = typeof checkId === "string" && ID_PATTERN.test(checkId);
  const result = useQuery(api.checks.get, valid ? { checkId: checkId as Id<"tournamentChecks"> } : "skip");
  const failed = useQuery(api.checks.failedRegistrants, valid ? { checkId: checkId as Id<"tournamentChecks"> } : "skip");
  const cancel = useMutation(api.checks.cancel);
  // Les signalements sont paginés : un gros tournoi en produirait trop pour une seule lecture.
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const { results: suspects, status: pageStatus, loadMore } = usePaginatedQuery(
    api.checks.suspects,
    valid ? { checkId: checkId as Id<"tournamentChecks">, search: deferredSearch.trim() || undefined } : "skip",
    { initialNumItems: 50 },
  );
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [exporting, setExporting] = useState(false);

  if (!valid) return <EmptyState title="Contrôle introuvable" description="Ce lien ne correspond à aucune analyse." />;
  if (result === undefined) return <TableSkeleton />;
  if (!result.check) return <EmptyState title="Contrôle introuvable" description="Cette analyse n’existe plus." />;

  const check = result.check;
  const running = RUNNING.includes(check.status);

  const stop = async () => {
    setCancelling(true); setError("");
    try { await cancel({ checkId: check._id }); }
    catch (reason) { setError(errorMessage(reason)); }
    finally { setCancelling(false); }
  };

  const exportDiscord = async () => {
    setExporting(true); setMessage(""); setError("");
    try {
      const exported = await convex.query(api.checks.exportDiscordIds, { checkId: check._id });
      const count = downloadDiscordIds(exported.label, exported.ids);
      if (count === 0) setError("Aucun ID Discord exploitable dans ce contrôle.");
      else setMessage(`${count} ID Discord exporté${count > 1 ? "s" : ""}.`);
    } catch (reason) { setError(errorMessage(reason)); }
    finally { setExporting(false); }
  };

  const copyReason = async () => {
    try {
      await copyText(banReason());
      setMessage("Motif copié."); setError("");
    } catch { setError("Le motif n’a pas pu être copié."); }
  };

  return <>
    <Link to="/controles" className="back-link"><ArrowLeft size={16} /> Retour aux contrôles</Link>
    <PageHeader
      title={check.tournamentId || check.fileName || "Détail du contrôle"}
      description={`Lancé le ${formatDate(check.createdAt)} · moteur ${check.algorithmVersion}`}
      actions={
        <>
          <Badge tone={check.status === "completed" ? "success" : check.status === "failed" ? "danger" : "warning"}>{STATUS_LABELS[check.status]}</Badge>
          {running && <Button variant="secondary" loading={cancelling} onClick={() => void stop()}>Annuler l’analyse</Button>}
        </>
      }
    />
    {error && <Notice tone="error">{error}</Notice>}
    {message && <Notice tone="success">{message}</Notice>}
    {running && (
      <Card className="progress-card">
        <div className="card-heading"><h2>Analyse des inscrits en cours</h2></div>
        <ProgressBar
          value={check.processedRegistrants}
          total={check.totalRegistrants}
          label="Progression de l’analyse"
        />
        <p className="muted">Vous pouvez quitter cette page : l’analyse se poursuit et le résultat sera conservé.</p>
      </Card>
    )}
    <div className="metric-grid">
      <Card className="metric-card"><span className="metric-icon metric-icon--muted"><Users /></span><div><span>Inscrits analysés</span><strong>{formatNumber(check.processedRegistrants)}</strong><small>Lignes comparées à la blacklist</small></div></Card>
      <Card className="metric-card"><span className="metric-icon metric-icon--danger"><ShieldAlert /></span><div><span>Blacklistés</span><strong>{formatNumber(result.stats.confirmed)}</strong><small>Epic ID ou Discord ID identique à une entrée</small></div></Card>
      <Card className="metric-card"><span className="metric-icon metric-icon--warning"><ShieldAlert /></span><div><span>Contournements de ban</span><strong>{formatNumber(result.stats.evasions)}</strong><small>Revenus sous un autre compte, ajoutés automatiquement</small></div></Card>
      <Card className="metric-card"><span className="metric-icon metric-icon--success"><CheckCircle2 /></span><div><span>Hors de cause</span><strong>{formatNumber(result.stats.clean)}</strong><small>Aucune correspondance avec la blacklist</small></div></Card>
    </div>
    {result.stats.evasions > 0 && (
      <Notice tone="warning">
        <strong>{result.stats.evasions} contournement{result.stats.evasions > 1 ? "s" : ""} de ban</strong> — ce{result.stats.evasions > 1 ? "s" : ""} joueur{result.stats.evasions > 1 ? "s reviennent" : " revient"} sous un autre compte.
      </Notice>
    )}
    {check.errorsCount > 0 && <Notice tone="error">{check.errorsCount} inscrit{check.errorsCount > 1 ? "s n’ont" : " n’a"} pas pu être analysé{check.errorsCount > 1 ? "s" : ""}.</Notice>}
    {check.errorMessage && <Card className="error-card"><strong>Échec de l’analyse</strong><p>{check.errorMessage}</p></Card>}
    {failed && failed.length > 0 && (
      <Card className="error-card">
        <strong>{failed.length} inscrit{failed.length > 1 ? "s" : ""} non analysé{failed.length > 1 ? "s" : ""}</strong>
        <ul className="failed-list">
          {failed.map((row) => (
            <li key={row._id}>
              <strong>{row.epicName || row.wlsName || row.epicId || `Ligne ${row.rowNumber}`}</strong>
              <small>{row.processingError || "Motif inconnu"}</small>
            </li>
          ))}
        </ul>
      </Card>
    )}
    <Card className="control-tools">
      <div className="search-field">
        <Search size={17} aria-hidden="true" />
        <Input
          value={search} onChange={(event) => setSearch(event.target.value)}
          placeholder="Rechercher un ID ou un pseudo dans ce contrôle…"
          aria-label="Rechercher un joueur dans ce contrôle"
        />
      </div>
      <BanReasonPicker />
      <Button variant="secondary" loading={exporting} onClick={() => void exportDiscord()}>
        <Download size={15} /> Exporter les IDs Discord
      </Button>
    </Card>
    {suspects.length === 0 ? (
      <>
        <div className="section-heading"><h2>Joueurs repérés</h2></div>
        <EmptyState
          title={running || pageStatus === "LoadingFirstPage" ? "Analyse en cours" : search ? "Aucun résultat" : "Aucun joueur blacklisté"}
          description={running ? "Les joueurs repérés apparaîtront ici au fil de l’analyse." : search ? "Aucun ID ou pseudo ne correspond à cette recherche." : "Aucun inscrit ne correspond à la blacklist."}
        />
      </>
    ) : (
      <>
        <ResultTable
          titre="Joueurs blacklistés"
          sousTitre="Epic ID ou Discord ID identique à une entrée de la blacklist : c’est le même compte."
          lignes={suspects}
          onCopyReason={copyReason}
        />
        {pageStatus === "CanLoadMore" && (
          <div className="load-more"><Button variant="secondary" onClick={() => loadMore(50)}>Afficher plus</Button></div>
        )}
      </>
    )}
  </>;
}

function ResultTable({ titre, sousTitre, lignes, onCopyReason }: {
  titre: string; sousTitre: string;
  lignes: Array<any>;
  onCopyReason: () => Promise<void>;
}) {
  if (lignes.length === 0) return null;
  return <>
    <div className="section-heading">
      <div><h2>{titre} <span className="heading-count">{lignes.length}</span></h2><p className="muted">{sousTitre}</p></div>
    </div>
    <Card className="table-card">
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th scope="col">Joueur</th>
              <th scope="col">Motif du bannissement</th>
              <th scope="col">Preuve du rapprochement</th>
              <th scope="col">Statut</th>
              <th scope="col">Action rapide</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((suspect) => {
              const player = suspect.registrant as Record<string, string>;
              const contournement = suspect.banEvasion ? describeEvasionProof(suspect.evidence) : null;
              // Les signaux se répètent une fois par fiche concernée : on ne garde qu'un exemplaire.
              const signaux = [...new Set(suspect.evidence.map((item: any) => signalLabel(item.signal)))] as string[];
              return (
                <tr key={suspect._id} className={suspect.banEvasion ? "row--evasion" : undefined}>
                  <td>
                    <div className="identity-cell">
                      <strong>{player.epicName || player.discordName || player.wlsName || "Joueur"}</strong>
                      <span className="identity-cell__id">{player.epicId || player.discordId || "—"}</span>
                    </div>
                  </td>
                  <td className="wrap-cell">
                    {suspect.sanction ? <>
                      <Badge>{CATEGORY_LABELS[suspect.sanction.category as keyof typeof CATEGORY_LABELS]}</Badge>
                      <div className="muted" style={{ marginTop: 6 }}>{suspect.sanction.reason || "Sans motif"}</div>
                      {suspect.sanction.serversBan && <small className="muted">{suspect.sanction.serversBan}</small>}
                    </> : "—"}
                  </td>
                  <td>
                    {contournement ? (
                      <div className="evasion-proof">
                        <div className="evasion-proof__line">
                          <span>{contournement.partageLabel}</span>
                          <code>{contournement.partageValeur ?? "—"}</code>
                        </div>
                        <div className="evasion-proof__line evasion-proof__line--change">
                          <span>{contournement.changeLabel}</span>
                          <code>{contournement.anciens.join(" / ")}</code>
                          <ArrowRight size={13} aria-hidden="true" />
                          <code>{contournement.nouveau}</code>
                        </div>
                      </div>
                    ) : (
                      <div className="evidence-list">
                        {signaux.map((label) => <span key={label}>{label}</span>)}
                      </div>
                    )}
                  </td>
                  <td>
                    {suspect.banEvasion
                      ? <Badge tone="evasion">Contournement de ban</Badge>
                      : <Badge tone="danger">Déjà blacklisté</Badge>}
                  </td>
                  <td>
                    {suspect.sanction ? (
                      <Button variant="secondary" onClick={() => void onCopyReason()}>
                        <Copy size={14} /> Copier le motif
                      </Button>
                    ) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  </>;
}
